import {User, UserSchema} from '../schema/User';
import {getEnv} from '../config/environment';

export function isReonboardingEnabled(): boolean {
  const currentMode = getEnv().MODE;
  const isDevOrStaging =
    currentMode === 'development' || currentMode === 'staging';

  return (
    isDevOrStaging || localStorage.getItem('enableReonboarding') === 'true'
  );
}

/**
 * Determines if a user should be redirected to the onboarding flow
 * based on whether they have completed their profile information.
 *
 * Checks if the user is missing any of these essential profile fields:
 * - firstName
 * - birthDate
 * - gender
 * - location
 * - content creator or influencer status
 * - specialties (tags)
 * - capabilities (content preferences)
 * - bio
 * - portfolio media items (only for content creators)
 * - social identities
 *
 * @param user The user object to check
 * @returns true if the user should be redirected to onboarding, false otherwise
 */
// eslint-disable-next-line import/prefer-default-export
export const shouldRedirectToOnboarding = (
  inputUser: User | undefined | null
): boolean => {
  const parsedUser = UserSchema.safeParse(inputUser);
  if (!parsedUser.success) {
    return false;
  }

  const user = parsedUser.data;

  if (!user || !isReonboardingEnabled()) return false;

  // Check if firstName is missing or empty
  const isFirstNameMissing = !user.firstName || user.firstName.trim() === '';

  // Check if lastName is missing or empty
  const isLastNameMissing = !user.lastName || user.lastName.trim() === '';

  // Check if birthDate is missing
  const isBirthDateMissing = !user.birthDate;

  // Check if gender is missing
  const isGenderMissing = !user.profile?.gender;

  // Check if location is missing
  const isLocationMissing = !user.profile?.location && !user.profile?.locale;

  // Check if the user has specified whether they are a content creator or influencer
  const isCreatorStatusMissing =
    (user.profile?.contentCreator === null ||
      user.profile?.contentCreator === undefined) &&
    (user.profile?.influencer === null ||
      user.profile?.influencer === undefined);

  // Check if user has specialties (tags)
  const hasNoSpecialties =
    !user.profile?.tags || user.profile.tags.length === 0;

  // Check if user has capabilities (content preferences)
  const hasNoCapabilities =
    !user.profile?.contentPreferences ||
    user.profile.contentPreferences.length === 0;

  // Check if collaboration rate is missing
  const isMinCollaborationRateMissing =
    user.profile?.minCollaborationRate === undefined;

  // Check if bio is missing or empty
  const isBioMissing = !user.profile?.bio || user.profile.bio.trim() === '';

  // Check if portfolio media items are missing (only for content creators)
  const isContentCreator = user.profile?.contentCreator === true;
  const hasNoPortfolioItems =
    isContentCreator &&
    (!user.profile?.portfolioMediaItems ||
      user.profile.portfolioMediaItems.length === 0);

  // Check if social identities are missing
  const isInfluencer = user.profile?.influencer === true;
  const isInfluencerWithoutIdentities =
    isInfluencer && (!user.identities || user.identities.length === 0);

  // Return true if any of the essential fields are missing
  return (
    isFirstNameMissing ||
    isLastNameMissing ||
    isBirthDateMissing ||
    isGenderMissing ||
    isLocationMissing ||
    isCreatorStatusMissing ||
    hasNoSpecialties ||
    hasNoCapabilities ||
    isBioMissing ||
    isMinCollaborationRateMissing ||
    hasNoPortfolioItems ||
    isInfluencerWithoutIdentities
  );
};
